import { createContext, useContext, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useApiGet } from './useApiGet.js';
import { tenantBasePath } from './tenant.js';
import { amountUnitLabel, formatRialFa, formatRialFaWithUnit } from './finance-display.js';
import {
  TENANT_SETTING_DEFAULTS,
  type TenantSettings,
  type TenantSettingsResponse,
} from './types/tenantSettings.js';
import type { AmountDisplayUnit } from '@sakhtar/money';

export type TenantSettingsLoadStatus = 'loading' | 'error' | 'ready';

interface TenantSettingsState {
  readonly settings: TenantSettings;
  readonly status: TenantSettingsLoadStatus;
}

const TenantSettingsContext = createContext<TenantSettingsState>({
  settings: TENANT_SETTING_DEFAULTS,
  status: 'loading',
});

/**
 * Loads allowlisted tenant settings for `slug`. While the GET is in flight (or
 * on slug change) consumers see the rial defaults — never the previous
 * tenant's toman binding. Amount-bearing pages should prefer
 * {@link useAmountDisplay}; they re-render when the load completes.
 */
export function TenantSettingsProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const result = useApiGet<TenantSettingsResponse>(
    `${tenantBasePath(slug)}/api/v1/tenant-settings`,
    [slug],
  );
  const state: TenantSettingsState =
    result.status === 'ready'
      ? { settings: result.data.settings, status: 'ready' }
      : {
          settings: TENANT_SETTING_DEFAULTS,
          status: result.status === 'error' ? 'error' : 'loading',
        };

  return <TenantSettingsContext.Provider value={state}>{children}</TenantSettingsContext.Provider>;
}

/** Print routes sit outside AuthenticatedShell and must load settings themselves. */
export function PrintTenantSettings({ children }: { children: ReactNode }) {
  const { slug } = useParams();
  if (!slug) return children;
  return <TenantSettingsProvider slug={slug}>{children}</TenantSettingsProvider>;
}

export function useTenantSettings(): TenantSettings {
  return useContext(TenantSettingsContext).settings;
}

export function useTenantSettingsStatus(): TenantSettingsLoadStatus {
  return useContext(TenantSettingsContext).status;
}

/** Unit, label, and formatter from the same loaded setting. Defaults to rial until ready. */
export function useAmountDisplay(): {
  readonly unit: AmountDisplayUnit;
  readonly label: 'ریال' | 'تومان';
  readonly formatAmount: (digits: string) => string;
  readonly formatWithUnit: (digits: string) => string;
  readonly ready: boolean;
} {
  const { settings, status } = useContext(TenantSettingsContext);
  const unit = settings.amountDisplayUnit;
  return {
    unit,
    label: amountUnitLabel(unit),
    formatAmount: (digits) => formatRialFa(digits, unit),
    formatWithUnit: (digits) => formatRialFaWithUnit(digits, unit),
    ready: status !== 'loading',
  };
}
